define(() => {
    return (AvailabilityService, LoadSaveService, $q, TranslateService, ViewModelService, ViewModelContext, ActionMessageUtil)=> {
        const translateFn = TranslateService.instant;

        function wrappedLoadSave(action, model) {
            const promise = action.call(undefined, null, model);
            return promise.then(submissionDTO => ViewModelService.create(submissionDTO, 'pc', 'edge.capabilities.quote.dto.QuoteDataDTO', ViewModelContext));
        }

        const ACTION_MSG = {
            cancelPrompt: {
                noSaveRequired: 'quoteandbind.common.config.FlowConfig.The information you have entered will not be saved',
                saveDraft: 'quoteandbind.common.config.FlowConfig.The information previously entered will be stored as a draft quote submission',
                saveOffering: 'quoteandbind.common.config.FlowConfig.The information previously entered will be stored as a quote submission',
            },
            checkAvailability: {
                error: {
                    title: 'quoteandbind.common.config.FlowConfig.Unable to request a quote',
                    message: 'quoteandbind.common.config.FlowConfig.Sorry we are unable to quote for this location and start date.',
                },
                progress: {
                    title: 'quoteandbind.common.config.FlowConfig.Checking product availability',
                    message: 'quoteandbind.common.config.FlowConfig.Checking if a quote can be provided for that location',
                },
            },
            checkPeriodStartDateChanged: {
                error: {
                    title: 'quoteandbind.common.config.FlowConfig.Unable to request a quote',
                    message: 'quoteandbind.common.config.FlowConfig.Sorry coverage is not available for the effective date specified.',
                },
                progress: {
                    title: 'quoteandbind.common.config.FlowConfig.Checking product availability',
                    message: 'quoteandbind.common.config.FlowConfig.Checking if coverage is available for that date.',
                },
            },
            createSubmission: {
                error: {
                    title: 'quoteandbind.common.config.FlowConfig.Unable to create submission',
                    message: 'quoteandbind.common.config.FlowConfig.An error occurred while attempting to create the quote submission.',
                },
                progress: {
                    message: 'quoteandbind.common.config.FlowConfig.Creating a new quote submission...',
                },
            },
            saveSubmission: {
                error: {
                    title: 'quoteandbind.common.config.FlowConfig.Unable to save submission',
                    message: 'quoteandbind.common.config.FlowConfig.An error occurred while attempting to save quote submission.',
                },
                progress: {
                    message: 'quoteandbind.common.config.FlowConfig.Saving quote submission details...',
                },
            },
            saveAndQuoteSubmission: {
                error: {
                    title: 'quoteandbind.common.config.FlowConfig.Unable to generate a quote',
                    message: 'quoteandbind.common.config.FlowConfig.An error occurred while attempting to calculate a quote.',
                },
                progress: {
                    message: 'quoteandbind.common.config.FlowConfig.Calculating Quote...',
                },
            },
            bindSubmission: {
                error: {
                    title: 'quoteandbind.common.config.FlowConfig.Unable to purchase quote',
                    message: 'quoteandbind.common.config.FlowConfig.An error occurred while attempting to bind your quote submission',
                },
                progress: {
                    message: 'quoteandbind.common.config.FlowConfig.Processing Payment...',
                },
            },
        };
        const ACTIONS = {
            checkAvailability: (model) => {
                //wrap the availablity check promise in a promise that returns the model with the policy address set
                return new $q((resolve) => {
                    resolve(model);
                    // Basler is not using Product Availability fnc
                });
            },
            cancel: (model) => {
                if (!model.quoteID.value) {
                    //no submission was created so just exit without saving
                    return $q.resolve(model);
                }
                if (!model.quoteData.value) {
                    return LoadSaveService.saveSubmission(null, model.value);
                }
                return LoadSaveService.saveQuotedSubmission(null, model.value);
            },
            createSubmission: ({value: submission}) => wrappedLoadSave(LoadSaveService.createSubmission, submission),
            saveDraftSubmission: ({value: submission}) => wrappedLoadSave(LoadSaveService.saveSubmission, submission),
            saveQuotedSubmission: ({value: submission}) => wrappedLoadSave(LoadSaveService.saveQuotedSubmission, submission),
            saveAndQuoteSubmission: ({value: submission}) => wrappedLoadSave(LoadSaveService.saveAndQuoteSubmission, submission),
            bindSubmission: ({value: submission}) => wrappedLoadSave(LoadSaveService.bindSubmission, submission),
        };

        return {
            globalExits(step, junction) {
                junction('error');

                junction('returnHome');

                junction('cancel')
                    .data({
                        confirmationMsg(model) {
                            if (!model.quoteID.value) {
                                return ACTION_MSG.cancelPrompt.noSaveRequired;
                            }
                            if (!model.quoteData.value) {
                                return ACTION_MSG.cancelPrompt.saveDraft;
                            }
                            return ACTION_MSG.cancelPrompt.saveOffering;
                        },
                    })
                    .onNext.doAction(ACTIONS.cancel, ACTION_MSG.saveSubmission).thenGoTo('returnHome', 'error');

                step('contactUs');
            },
            availabilityCheck(step, junction) {
                junction('checkAvailability', true)
                    .onNext.doAction(ACTIONS.checkAvailability, ACTION_MSG.checkAvailability).thenGoTo('productAvailable', 'error');

                junction('productAvailable');

            },
            needToSaveCheck(step, junction) {
                junction('checkNewSubmissionOrIfPeriodStartDateHasChanged', true)
                    .onNext
                    .branch((model, args) => {
                        if (!model.quoteID.value) {
                            return 'newSubmission';
                        }
                        if (args.periodStartDateChanged) {
                            return 'periodStartDateChanged';
                        }
                        return 'needToSaveCheckSuccess';
                    });

                junction('newSubmission')
                    .onNext.doAction(ACTIONS.createSubmission, ACTION_MSG.createSubmission).thenGoTo('needToSaveCheckSuccess', 'error');


                junction('periodStartDateChanged')
                    .onNext.doAction(ACTIONS.checkAvailability, ACTION_MSG.checkPeriodStartDateChanged).thenGoTo('needToSaveCheckSuccess', 'error');

                junction('needToSaveCheckSuccess');
            },
            quote(step, junction) {
                junction('generateQuote', true)
                    .onNext.doAction(ACTIONS.saveAndQuoteSubmission, ActionMessageUtil.quoteFailMessage).thenGoTo('quoteDetails', 'error');

                step('quoteDetails')
                    .onNext.goTo('offeringSelected', 'error')
                    .skipOnResumeIf(model => model.bindData.policyNumber.value)
                    .label(() => translateFn('quoteandbind.wizard.step.Quote'));

                junction('offeringSelected');
            },
            bindAndPay(step, junction) {
                step('paymentDetails', true)
                    .onNext.doAction(ACTIONS.bindSubmission, ACTION_MSG.bindSubmission).thenGoTo('confirmation', 'error')
                    .skipOnResumeIf(model => model.bindData.policyNumber.value)
                    .isValid(model => model.aspects.valid && model.aspects.subtreeValid)
                    .label(() => translateFn('quoteandbind.wizard.step.Payment Details'));

                junction('confirmation');
            },
        };
    };
});


// WEBPACK FOOTER //
// ./app/customer/js/src/edge/quoteandbind/common/config/FlowConfig.js